import { Table, Text } from '@nimbus-ds/components';
import { TrashIcon } from '@nimbus-ds/icons';
import { MetaplanItemResponseDto } from '@tiendanube/common';
import { useToastProgress } from 'App/components/ToastProgressContext';
import { ActionIconButton, InternalNavLink } from 'commons/components';
import { useModal, useToast } from 'commons/hooks';
import { useTranslationCatalog } from 'domains/Catalog/hooks';
import {
  DeleteMetaplanModal,
  SubscriptionConditions,
} from 'domains/Catalog/Subscriptions/components';
import { useDeleteMetaplanMutation } from 'domains/Catalog/Subscriptions/subscriptionsApi';

interface MetaplansListDesktopRowProps {
  metaplan: MetaplanItemResponseDto;
}

export function MetaplansListDesktopRow({
  metaplan: { id, name, subscriptionOptions },
}: Readonly<MetaplansListDesktopRowProps>) {
  const t = useTranslationCatalog();
  const [showDeleteModal, openDeleteModal, closeDeleteModal] = useModal();
  const [deleteMetaplan, { isLoading }] = useDeleteMetaplanMutation();
  const { addToast } = useToast();
  const { addToastProgress, closeToastProgress } = useToastProgress();

  const handleDelete = async (): Promise<void> => {
    closeDeleteModal();
    addToastProgress({
      label: t('subscriptions.deleteModal.progress'),
    });
    try {
      await deleteMetaplan(id).unwrap();
      closeToastProgress();
      addToast({
        label: t('subscriptions.deleteModal.success'),
        appearance: 'success',
      });
    } catch {
      closeToastProgress();
      addToast({
        label: t('subscriptions.deleteModal.error'),
        appearance: 'danger',
      });
    }
  };

  return (
    <>
      <Table.Row>
        <Table.Cell backgroundColor="neutral-background">
          <InternalNavLink to={id}>
            <Text
              color="primary-interactive"
              fontWeight="regular"
              lineClamp={1}
            >
              {name}
            </Text>
          </InternalNavLink>
        </Table.Cell>
        <Table.Cell backgroundColor="neutral-background">
          <SubscriptionConditions subscriptionOptions={subscriptionOptions} />
        </Table.Cell>
        <Table.Cell backgroundColor="neutral-background">
          <ActionIconButton
            content={t('subscriptions.table.deleteMetaplan')}
            source={<TrashIcon />}
            onClick={openDeleteModal}
          />
        </Table.Cell>
      </Table.Row>
      <DeleteMetaplanModal
        show={showDeleteModal}
        onClose={closeDeleteModal}
        onDelete={handleDelete}
        isLoading={isLoading}
      />
    </>
  );
}
